<!-- eslint-disable @intlify/vue-i18n/no-missing-keys -->
<script setup lang="ts">
import {
  IMediaCenterSelection,
  MediaCenterAllowedExtensions,
  MediaCenterSubCategory,
} from "@/helpers/mediaCenter.type";
import { triggerMediaCenterOpen } from "@/helpers/mediaCenterUtil";
import { useAppStore } from "@/store/app";
import { ImagePlusIcon } from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UITextSmMedium } from "@gohighlevel/ghl-ui";
import { useI18n } from "vue-i18n";
import { ImageIcon } from "../../util/constants";

const { t } = useI18n();
const store = useAppStore();
const emit = defineEmits(["update:value"]);

const props = defineProps({
  element: {
    type: Object,
    required: true,
  },
  index: {
    type: Number,
    required: true,
  },
  mediaCenterSubCategory: {
    type: String as () => MediaCenterSubCategory,
    required: true,
  },
});

const handleMediaCenterSelection = (data: IMediaCenterSelection) => {
  if (!data?.url) return;
  emit("update:value", data.url);
  (props.element as any).url = data.url;
  store.anyUnsavedChanges = true;
  store.addToHistory();
};

const onInputClick = (e: Event) => {
  e.preventDefault();

  triggerMediaCenterOpen({
    appHandshake: store.appHandshake,
    settings: {
      allowedExtensions: [MediaCenterAllowedExtensions.IMAGE],
      subCategory: props.mediaCenterSubCategory,
    },
    onSelect: handleMediaCenterSelection,
  });
};
</script>

<template>
  <div>
    <div
      v-if="!('url' in element)"
      class="form-control !flex flex-col items-center justify-center gap-3 !py-[32px]"
      :id="`image-upload-${props.element.id || props.element.uuid}-${
        props.index
      }`"
    >
      <div>
        <ImageIcon class="h-8 w-8" />
      </div>
      <div class="flex flex-col items-center justify-center gap-1 text-center">
        <UITextSmMedium
          class="text-center font-sans text-[13px] font-semibold not-italic leading-[18px] tracking-normal text-[#101828]"
          >{{ t("fields.other.imageUploadPlaceholder") }}</UITextSmMedium
        >
      </div>
      <div class="flex items-center justify-center gap-2">
        <UIButton
          id="media-library-button"
          class="media-library-button rounded"
          type="primary"
        >
          <ImagePlusIcon class="add-elements-icon h-5 w-5"></ImagePlusIcon>
          <UITextSmMedium class="add-products-text pl-2">
            {{ t("fields.other.imageUploadButtonText") }}</UITextSmMedium
          >
          <input
            v-if="!('url' in props.element)"
            :aria-label="t('fields.other.imageUploadButtonText')"
            type="file"
            class="form-control absolute left-0 top-0 h-full w-full cursor-pointer opacity-0"
            :placeholder="t('common.imagePlaceholder')"
            :name="props.element.tag"
            accept="image/*"
            @click="onInputClick"
          />
        </UIButton>
      </div>
    </div>

    <div
      v-else-if="'url' in props.element"
      class="form-builder--item form-builder--image"
      :align="!props.element.align ? 'center' : props.element.align"
    >
      <img
        :src="props.element.url"
        :alt="props.element.alt"
        :style="{
          ...(props.element.width && props.element.width > 0
            ? { width: props.element.width + 'px' }
            : { maxWidth: '100%' }),
          ...(props.element.height && props.element.height > 0
            ? { height: props.element.height + 'px' }
            : {}),
        }"
      />
    </div>
  </div>
</template>
